import { z } from "zod";
import { PROFESSION_IDS } from "./professions/index.ts";
import { MAX_CHARS, MIN_CHARS } from "./seo.ts";

// Every API input is validated with these (brief §4). They are shared so the
// web forms and the server reject the same things with the same messages.

const text = (max: number) => z.string().trim().max(max, `${max}文字以内で入力してください`);
const required = (max: number, label: string) => text(max).min(1, `${label}を入力してください`);
const httpsUrl = z
  .string()
  .trim()
  .url("URL の形式が正しくありません")
  .refine((u) => u.startsWith("https://"), "https:// で始まる URL を入力してください");

export const TONES = ["formal", "friendly", "authoritative", "empathetic"] as const;
export const TONE_LABELS: Record<(typeof TONES)[number], string> = {
  formal: "丁寧・堅実",
  friendly: "親しみやすい",
  authoritative: "明快・論理的",
  empathetic: "寄り添う",
};

const volume = z.coerce.number().int("整数で入力してください").min(0, "0以上で入力してください").max(10_000_000, "大きすぎます");
const childCount = z.coerce.number().int("整数で入力してください").min(0, "0以上で入力してください").max(1000, "1000以下で入力してください");
const childRange = ([min, max]: [number, number]) =>
  z
    .object({ min: childCount, max: childCount })
    .refine((r) => r.min <= r.max, { message: "子記事の本数は、下限を上限以下にしてください" })
    .default({ min, max });

export const REVIEW_MODES = ["app", "wordpress"] as const;
export type ReviewMode = (typeof REVIEW_MODES)[number];
export const REVIEW_MODE_LABELS: Record<ReviewMode, string> = {
  app: "この管理画面で確認してから WordPress に下書き投稿",
  wordpress: "WordPress に下書き投稿してから、WordPress の画面で確認",
};

export const OfficeCreateInput = z.object({
  name: required(100, "事務所名"),
  profession: z.enum(PROFESSION_IDS).default("tax_accountant"),
});

export const OfficeSettingsInput = z.object({
  siteName: text(100).default(""),
  region: text(100).default(""),
  specialties: z.array(text(40).min(1)).max(20).default([]),
  tone: z.enum(TONES).default("formal"),
  firstPerson: text(20).default("当事務所"),
  notationRules: z.array(text(200).min(1)).max(50).default([]),
  ctaHeading: text(100).default("お気軽にご相談ください"),
  ctaText: text(500).default(""),
  ctaButtonLabel: text(40).default("無料相談を予約する"),
  ctaUrl: z.union([z.literal(""), httpsUrl]).default(""),
  brandColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "#RRGGBB 形式で入力してください").default("#1d4ed8"),
  disclaimer: text(1000).default(""),
  showAiDisclosure: z.boolean().default(false),
  defaultTargetChars: z.coerce.number().int().min(MIN_CHARS).max(MAX_CHARS).default(5000),

  // Keyword targeting (高岡さん, 2026-10-03): which keywords to put forward.
  /** Monthly search volume range of the keywords to aim for. */
  keywordVolumeMin: volume.default(500),
  keywordVolumeMax: volume.default(1200),
  /** Place names; a keyword containing one is local and shown even outside the range. */
  localTerms: z.array(text(40).min(1)).max(30).default([]),
  /** Difficulty levels to recommend (1 = easiest, 5 = hardest). */
  preferredLevels: z.array(z.number().int().min(1).max(5)).max(5).default([2, 3]),
  /**
   * The range of child articles each difficulty level may need (高岡さん,
   * 2026-10-05). Where in the range a keyword lands is computed from its
   * related keywords, intent breadth and the competition.
   */
  childRanges: z
    .object({ level1: childRange([0, 0]), level2: childRange([2, 5]), level3: childRange([10, 30]), level4: childRange([30, 80]), level5: childRange([50, 250]) })
    .default({ level1: { min: 0, max: 0 }, level2: { min: 2, max: 5 }, level3: { min: 10, max: 30 }, level4: { min: 30, max: 80 }, level5: { min: 50, max: 250 } }),

  /**
   * Where the tax accountant does the final check: in this app before the
   * WordPress draft ("app"), or in WordPress on the draft ("wordpress").
   */
  reviewMode: z.enum(REVIEW_MODES).default("app"),
  /** Articles to publish per month (高岡さん: 3–4); the dashboard tracks progress against it. */
  monthlyTarget: z.coerce.number().int("整数で入力してください").min(0, "0以上で入力してください").max(100, "100以下で入力してください").default(4),
  /**
   * おまかせ作成: once a day, start articles from recommended keywords until
   * the month's target is reached (they stop at outline review for a person).
   */
  autopilot: z.boolean().default(false),
  /**
   * The office's Search Console property this service reads: a URL prefix
   * (https://example.jp/) or a domain property (sc-domain:example.jp).
   * Empty until the office grants the service account access.
   */
  searchConsoleSite: z
    .string()
    .trim()
    .transform((v) => (/^https?:\/\/[^/]+$/.test(v) ? `${v}/` : v))
    .pipe(z.union([z.literal(""), z.string().regex(/^(https:\/\/[^\s/]+\/[^\s]*|sc-domain:[^\s/]+)$/, "https://example.jp/ か sc-domain:example.jp の形で入力してください")]))
    .default(""),
  /**
   * The office's Google Analytics (GA4) property this service reads, as
   * the numeric property ID (管理 → プロパティの詳細). Empty until the
   * office grants the service account access.
   */
  ga4PropertyId: z
    .string()
    .trim()
    .transform((v) => v.replace(/^properties\//, ""))
    .pipe(z.union([z.literal(""), z.string().regex(/^\d{6,12}$/, "GA4 のプロパティ ID は数字です（例：123456789）")]))
    .default(""),
}).refine((s) => s.keywordVolumeMin <= s.keywordVolumeMax, { path: ["keywordVolumeMax"], message: "上限は下限以上にしてください" });
export type OfficeSettingsInput = z.infer<typeof OfficeSettingsInput>;

/** 地図の数字 (Google ビジネスプロフィール) a 店舗 types in each month until the API is approved. */
export const StoreMetricsInput = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "月は YYYY-MM の形で指定してください"),
  mapViews: z.coerce.number().int().min(0).max(10_000_000).default(0),
  searchViews: z.coerce.number().int().min(0).max(10_000_000).default(0),
  directions: z.coerce.number().int().min(0).max(10_000_000).default(0),
  calls: z.coerce.number().int().min(0).max(10_000_000).default(0),
  websiteClicks: z.coerce.number().int().min(0).max(10_000_000).default(0),
  note: z.string().trim().max(500).default(""),
});
export type StoreMetricsInput = z.infer<typeof StoreMetricsInput>;

export const SupervisorInput = z.object({
  name: required(60, "氏名"),
  nameKana: text(60).default(""),
  /** Blank means the office's profession (税理士・弁護士 …). */
  title: text(60).default(""),
  registrationNumber: text(40).default(""),
  affiliation: text(100).default(""),
  profile: text(2000).default(""),
  /** The characters of the supervisor's seal (監修印); blank means the surname. */
  sealText: z.string().trim().refine((s) => [...s].length <= 4, "印の文字は4文字までです").default(""),
  isDefault: z.boolean().default(false),
});
export type SupervisorInput = z.infer<typeof SupervisorInput>;

/**
 * The characters on a supervisor's seal: as set, else the surname — the
 * name up to the first space (山田 太郎 → 山田), or its first two characters
 * when written without one.
 */
export function sealTextOf(s: { name: string; sealText?: string | null }): string {
  const set = s.sealText?.trim();
  if (set) return set;
  const name = s.name.trim();
  const surname = name.split(/[\s\u3000]+/)[0] ?? "";
  return [...(surname === name ? name : surname)].slice(0, surname === name ? 2 : 4).join("") || "監修";
}

export const WordpressConnectionInput = z.object({
  // https is enforced by the server in production; development uses a local http stand-in.
  siteUrl: z.string().trim().url("URL の形式が正しくありません").refine((u) => /^https?:\/\//.test(u), "https:// で始まる URL を入力してください"),
  username: required(100, "ユーザー名"),
  // WordPress shows application passwords as 24 characters in groups of four.
  applicationPassword: z
    .string()
    .transform((s) => s.replace(/\s+/g, ""))
    .pipe(z.string().regex(/^[A-Za-z0-9]{24}$/, "アプリケーションパスワードは24文字の英数字です（空白は無視します）")),
  categoryId: z.coerce.number().int().positive().nullable().default(null),
});
export type WordpressConnectionInput = z.infer<typeof WordpressConnectionInput>;

export const KeywordResearchInput = z.object({
  seeds: z.array(text(80).min(1)).min(1, "キーワードを1つ以上入力してください").max(10, "一度に調べられるのは10語までです"),
});

export const KeywordDecisionInput = z.object({ status: z.enum(["approved", "rejected", "candidate"]) });

export const ArticleCreateInput = z.object({
  keywordId: z.string().uuid(),
  supervisorId: z.string().uuid().nullable().default(null),
  /** Makes the new article a child (詳細記事) of this overview article. */
  parentArticleId: z.string().uuid().nullable().default(null),
  targetChars: z.coerce.number().int().min(MIN_CHARS).max(MAX_CHARS).optional(),
  notes: text(2000).default(""),
});

export const OutlineNodeSchema = z.object({
  id: z.string().min(1).max(40),
  level: z.union([z.literal(2), z.literal(3)]),
  heading: required(200, "見出し"),
  notes: text(1000).default(""),
});
export type OutlineNode = z.infer<typeof OutlineNodeSchema>;

export const OutlineUpdateInput = z.object({
  title: required(120, "タイトル"),
  outline: z.array(OutlineNodeSchema).min(3, "見出しを3つ以上にしてください").max(40),
});

export const FaqItemSchema = z.object({ question: required(300, "質問"), answer: required(2000, "回答") });
export type FaqItem = z.infer<typeof FaqItemSchema>;

export const ArticleUpdateInput = z
  .object({
    title: required(120, "タイトル"),
    bodyMd: z.string().max(100_000),
    metaDescription: text(300),
    faq: z.array(FaqItemSchema).max(20),
    supervisorId: z.string().uuid().nullable(),
    parentArticleId: z.string().uuid().nullable(),
    plannedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "日付の形式が正しくありません").nullable(),
    revisionNote: text(200),
  })
  .partial();

/** One section rewritten by AI on a person's instruction. */
export const SectionRewriteInput = z.object({
  version: z.number().int(),
  heading: required(200, "見出し"),
  instruction: required(500, "指示"),
});

/** Child articles to start under an overview article, by keyword. */
export const ArticleChildrenInput = z.object({
  keywords: z.array(text(80).min(1)).min(1, "キーワードを選択してください").max(10, "一度に作成できる子記事は10本までです"),
});

export const TransitionInput = z.object({
  to: z.string().min(1).max(40),
  note: text(1000).default(""),
});

export const CommentInput = z.object({
  body: required(2000, "コメント"),
  quote: text(500).default(""),
});

export const FindingUpdateInput = z.object({ status: z.enum(["open", "resolved", "dismissed"]) });

/** 知見帳: what the supervisor knows from practice, in short notes the AI weaves into articles. */
export const NOTE_KINDS = ["case", "faq", "tip", "caution"] as const;
export type NoteKind = (typeof NOTE_KINDS)[number];
export const NOTE_KIND_LABELS: Record<NoteKind, string> = { case: "事例", faq: "よくある相談", tip: "実務のコツ", caution: "注意点" };
export const NoteInput = z.object({
  kind: z.enum(NOTE_KINDS, { message: "種類を選んでください" }),
  body: required(2000, "メモ"),
  /** Themes it belongs to (相続税, 贈与 …), to find it for an article. */
  tags: z.array(required(20, "テーマ")).max(8, "テーマは8つまでです").default([]),
});
export type NoteInput = z.infer<typeof NoteInput>;

/** 監修の要点: the facts of an article the supervisor confirms one by one. */
export const REVIEW_POINT_KINDS = ["数字", "期限", "要件", "制度・特例", "手続き"] as const;
export type ReviewPointKind = (typeof REVIEW_POINT_KINDS)[number];
/** open = not yet looked at, confirmed = 確認済み, flagged = 要修正 (the supervisor wants it changed). */
export const ReviewPointUpdateInput = z.object({ status: z.enum(["open", "confirmed", "flagged"]) });

export const InviteInput = z.object({
  email: z.string().trim().email("メールアドレスの形式が正しくありません"),
  name: required(60, "氏名"),
});

export const PartnerAssignInput = z.object({ userId: z.string().min(1) });
